import type { DashboardKpi } from '@/types/dashboard';

export type KpiCardProps = Partial<Omit<DashboardKpi, 'id' | 'label' | 'value' | 'icon'>> & {
  label: string;
  value: string;
  icon: string;
  sparklineData?: number[];
};

interface ToneConfig {
  iconBg: string;
  iconShadow: string;
  glowBg: string;
  topBorder: string;
}

const toneMap: Record<NonNullable<DashboardKpi['tone']>, ToneConfig> = {
  primary: {
    iconBg: 'bg-gradient-to-br from-blue-500 via-blue-600 to-indigo-600 text-white',
    iconShadow: 'shadow-md shadow-blue-500/30 ring-2 ring-blue-500/20',
    glowBg: 'group-hover:bg-blue-500/[0.03]',
    topBorder: 'from-blue-500 to-indigo-600',
  },
  accent: {
    iconBg: 'bg-gradient-to-br from-emerald-500 via-teal-500 to-teal-600 text-white',
    iconShadow: 'shadow-md shadow-emerald-500/30 ring-2 ring-emerald-500/20',
    glowBg: 'group-hover:bg-emerald-500/[0.03]',
    topBorder: 'from-emerald-500 to-teal-500',
  },
  secondary: {
    iconBg: 'bg-gradient-to-br from-rose-500 via-rose-600 to-amber-500 text-white',
    iconShadow: 'shadow-md shadow-rose-500/30 ring-2 ring-rose-500/20',
    glowBg: 'group-hover:bg-rose-500/[0.03]',
    topBorder: 'from-rose-500 to-amber-500',
  },
  background: {
    iconBg: 'bg-gradient-to-br from-violet-500 via-purple-600 to-indigo-600 text-white',
    iconShadow: 'shadow-md shadow-violet-500/30 ring-2 ring-violet-500/20',
    glowBg: 'group-hover:bg-violet-500/[0.03]',
    topBorder: 'from-violet-500 to-purple-600',
  },
};

export default function KpiCard({ label, value, delta, trend, icon, tone = 'primary' }: KpiCardProps) {
  const currentTone = toneMap[tone] ?? toneMap.primary;

  return (
    <div
      className={`group relative overflow-hidden rounded-2xl border border-background-200/90 bg-background-50/95 p-5 transition-all duration-300 ease-out hover:-translate-y-1 hover:shadow-xl hover:shadow-foreground-950/5 ${currentTone.glowBg}`}
    >
      {/* Top subtle highlight stripe that expands on hover */}
      <div
        className={`absolute top-0 left-0 right-0 h-[2.5px] bg-gradient-to-r ${currentTone.topBorder} opacity-70 group-hover:opacity-100 transition-opacity duration-300`}
      />

      {/* Decorative ambient background radial blur */}
      <div className="pointer-events-none absolute -right-6 -top-6 h-28 w-28 rounded-full bg-gradient-to-br from-current to-transparent opacity-0 blur-2xl transition-opacity duration-500 group-hover:opacity-10" />

      {/* Top row: Icon and Trend/Delta badge */}
      <div className="flex items-start justify-between gap-3 relative z-10">
        <div
          className={`w-11 h-11 flex items-center justify-center rounded-xl transition-transform duration-300 ease-out group-hover:scale-110 group-hover:-rotate-2 ${currentTone.iconBg} ${currentTone.iconShadow}`}
        >
          <i className={`${icon} text-xl`}></i>
        </div>

        {delta && trend === 'up' && (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-500/10 text-emerald-700 border border-emerald-500/20 shadow-xs transition-transform duration-200 group-hover:scale-105">
            <i className="ri-arrow-right-up-line text-emerald-600 text-xs"></i>
            {delta}
          </span>
        )}

        {delta && trend === 'down' && (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-rose-500/10 text-rose-700 border border-rose-500/20 shadow-xs transition-transform duration-200 group-hover:scale-105">
            <i className="ri-arrow-right-down-line text-rose-600 text-xs"></i>
            {delta}
          </span>
        )}

        {delta && trend === 'flat' && (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-500/10 text-amber-800 border border-amber-500/20 shadow-xs transition-transform duration-200 group-hover:scale-105">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500"></span>
            </span>
            {delta}
          </span>
        )}
      </div>

      {/* Value and Label */}
      <div className="mt-4 relative z-10">
        <p className="font-heading text-3xl font-extrabold tracking-tight text-foreground-950 transition-colors group-hover:text-primary-600">
          {value}
        </p>
        <p className="mt-1 text-sm font-semibold text-foreground-700">{label}</p>
      </div>
    </div>
  );
}
